import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { ArrowLeft, Mail } from "lucide-react";

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
}

const BulkEmail = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", session.user.id)
        .single();

      if (profile) {
        setUserName(profile.full_name);
      }

      // Fetch students with emails
      const { data: studentData } = await supabase
        .from("students")
        .select("id, first_name, last_name, email")
        .not("email", "is", null)
        .order("first_name");

      if (studentData) {
        setStudents(studentData);
      }
    };
    checkAuth();
  }, [navigate]);

  const toggleStudent = (studentId: string) => {
    setSelectedStudents(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const toggleAll = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(s => s.id));
    }
  };

  const handleSendEmails = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Please fill in subject and body");
      return;
    }

    if (selectedStudents.length === 0) {
      toast.error("Please select at least one student");
      return;
    }

    setLoading(true);
    try {
      const selectedStudentData = students.filter(s => selectedStudents.includes(s.id));
      
      const { error } = await supabase.functions.invoke('send-bulk-email', {
        body: {
          students: selectedStudentData,
          subject: subject.trim(),
          body: body.trim(),
        }
      });

      if (error) throw error;

      toast.success(`Emails sent successfully to ${selectedStudents.length} students`);
      setSubject("");
      setBody("");
      setSelectedStudents([]);
    } catch (error: any) {
      toast.error(error.message || "Failed to send emails");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout userName={userName}>
      <div className="max-w-4xl mx-auto px-4">
        <Button
          variant="ghost"
          onClick={() => navigate("/dashboard")}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Mail className="h-6 w-6" />
              Send Bulk Email to Students
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="subject">Subject *</Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter email subject"
                maxLength={200}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="body">Message *</Label>
              <Textarea
                id="body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Enter your message here..."
                rows={8}
                maxLength={2000}
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Select Students ({selectedStudents.length} selected)</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={toggleAll}
                >
                  {selectedStudents.length === students.length ? "Deselect All" : "Select All"}
                </Button>
              </div>

              <div className="border rounded-lg p-4 max-h-96 overflow-y-auto space-y-2">
                {students.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No students with email addresses found
                  </p>
                ) : (
                  students.map((student, index) => (
                    <div 
                      key={student.id} 
                      className="flex items-center space-x-2 p-2 hover:bg-accent rounded stagger-item"
                      style={{ animationDelay: `${index * 30}ms` }}
                    >
                      <Checkbox
                        id={student.id}
                        checked={selectedStudents.includes(student.id)}
                        onCheckedChange={() => toggleStudent(student.id)}
                      />
                      <label
                        htmlFor={student.id}
                        className="flex-1 text-sm cursor-pointer"
                      >
                        {student.first_name} {student.last_name} ({student.email})
                      </label>
                    </div>
                  ))
                )}
              </div>
            </div>

            <Button
              onClick={handleSendEmails}
              disabled={loading || selectedStudents.length === 0}
              className="w-full"
            >
              {loading ? "Sending..." : `Send Email to ${selectedStudents.length} Student${selectedStudents.length !== 1 ? 's' : ''}`}
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default BulkEmail;